import React, { useState, useEffect, useRef } from "react";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
import { addLocale } from "primereact/api";
import { getCurrentWeek, dateToWeek, weekToDate } from "../utils/weekUtils";

interface WeekPickerProps {
  value?: number; // Week number (1-52)
  year?: number;
  onChange: (weekNumber: number, year: number) => void;
  disabled?: boolean;
  minWeek?: number; // Minimum selectable week
  minYear?: number; // Year for the minimum week
}

addLocale("nl", {
  firstDayOfWeek: 1,
});

export const WeekPicker: React.FC<WeekPickerProps> = ({
  value,
  year = new Date().getFullYear(),
  onChange,
  disabled = false,
  minWeek,
  minYear,
}) => {
  const [selectedWeek, setSelectedWeek] = useState<number>(
    value || getCurrentWeek()
  );
  const [selectedYear, setSelectedYear] = useState<number>(year);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [displayValue, setDisplayValue] = useState<string>("");
  const overlayRef = useRef<OverlayPanel>(null);

  // Update display value whenever week/year changes
  useEffect(() => {
    if (selectedWeek && selectedYear) {
      setDisplayValue(`Week ${selectedWeek}, ${selectedYear}`);
      const date = weekToDate(selectedYear, selectedWeek);
      setSelectedDate(date);
    }
  }, [selectedWeek, selectedYear]);

  // Update week when external value changes
  useEffect(() => {
    if (value !== undefined && value !== selectedWeek) {
      setSelectedWeek(value);
    }
  }, [value, selectedWeek]);

  // Update year when external year changes
  useEffect(() => {
    if (year !== selectedYear) {
      setSelectedYear(year);
    }
  }, [year, selectedYear]);

  // Calculate minimum date if minWeek and minYear are provided
  const getMinDate = (): Date | undefined => {
    if (minWeek !== undefined && minYear !== undefined) {
      const minDate = weekToDate(minYear, minWeek);
      // Add one week to make it exclusive (end week must be after start week)
      minDate.setDate(minDate.getDate() + 7);
      return minDate;
    }
    return undefined;
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      const { weekNumber, year: newYear } = dateToWeek(date);
      const monday = weekToDate(newYear, weekNumber);
      setSelectedDate(monday); // Always set to Monday of the selected week
      setSelectedWeek(weekNumber);
      setSelectedYear(newYear);
      onChange(weekNumber, newYear);
      if (overlayRef.current) {
        overlayRef.current.hide();
      }
    }
  };

  return (
    <div className="week-picker">
      <div className="field">
        <div className="p-inputgroup">
          <InputText
            id="week-input"
            value={displayValue}
            placeholder="Click to select a week"
            readOnly
            disabled={disabled}
            className="p-inputtext-lg"
            style={{
              textAlign: "center",
              fontWeight: "bold",
              cursor: disabled ? "not-allowed" : "pointer",
            }}
            onClick={(e) => {
              if (!disabled && overlayRef.current) {
                overlayRef.current.toggle(e);
              }
            }}
          />

          <Button
            icon="pi pi-calendar"
            className="p-button-outlined"
            disabled={disabled}
            onClick={(e) => {
              if (overlayRef.current) {
                overlayRef.current.toggle(e);
              }
            }}
          />
        </div>

        <OverlayPanel ref={overlayRef} style={{ width: "500px" }}>
          <Calendar
            value={selectedDate}
            onChange={(e) => handleDateChange(e.value as Date)}
            inline
            showWeek
            locale="nl"
            className="week-calendar"
            minDate={getMinDate()}
          />
        </OverlayPanel>
      </div>

      <style>
        {`
          .week-calendar .p-datepicker table td {
            text-align: center;
          }
          .week-calendar .p-datepicker .p-datepicker-weekheader {
            background-color: var(--surface-200);
            font-weight: bold;
            font-size: 0.8rem;
          }
          .week-calendar .p-datepicker table td:hover {
            background-color: var(--primary-50);
          }
          .week-calendar .p-datepicker table td.p-highlight {
            background-color: var(--primary-color);
            color: var(--primary-color-text);
          }
        `}
      </style>
    </div>
  );
};
